import React from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, CircularProgress } from '@mui/material';
import { WarningAmber, Error as ErrorIcon } from '@mui/icons-material';
import CutoffDialog from './CutoffDialog';

// Groups every modal dialog rendered by ProgramSettings so the main component's
// JSX stays focused on page structure. Receives a single `dialogProps` object.
const ProgramSettingsDialogs = ({ dialogProps }) => {
  const {
    // Cutoff add/modify dialog
    cutoffDialogOpen,
    getCutoffDialogTitle,
    cutoffDialogValues,
    cutoffDialogErrors,
    cutoffDialogSaving,
    analyteLookupOptions,
    closeCutoffDialog,
    handleSaveCutoffDialog,
    handleCutoffDialogValueChange,

    // Unsaved changes (tab switching)
    unsavedDialogOpen,
    handleUnsavedDialogNo,
    handleUnsavedDialogYes,

    // Unsaved changes (route navigation)
    blocker,
    handleCancelNavigation,
    handleAcceptNavigation,

    // Delete cutoff confirm/success
    cutoffDeleteConfirmOpen,
    handleCancelDeleteCutoff,
    handleConfirmDeleteCutoff,
    cutoffDeleteSaving,

    // Delete ALL manual cutoffs confirm
    deleteAllCutoffsConfirmOpen,
    handleCancelDeleteAllCutoffs,
    handleConfirmDeleteAllCutoffs,
    deleteAllCutoffCount,

    // Disable-conditions warning
    cutoffWarningDialogOpen,
    handleCancelCutoffWarning,
    cutoffWarningConditions,
    getCutoffConditionDisplayName,
    handleConfirmCutoffWarning,
    conditionsLoading,

    // Import cutoffs validated (pending apply) / no-changes / error
    importCutoffsValidatedOpen,
    setImportCutoffsValidatedOpen,
    importCutoffsNoChangesOpen,
    setImportCutoffsNoChangesOpen,
    importCutoffsErrorOpen,
    setImportCutoffsErrorOpen,
    importCutoffsErrorMessages,
    pendingImportConditions,
  } = dialogProps;

  return (
    <>
      <CutoffDialog
        open={cutoffDialogOpen}
        title={getCutoffDialogTitle()}
        values={cutoffDialogValues}
        errors={cutoffDialogErrors}
        saving={cutoffDialogSaving}
        analyteLookupOptions={analyteLookupOptions}
        onClose={closeCutoffDialog}
        onSave={handleSaveCutoffDialog}
        onValueChange={handleCutoffDialogValueChange}
      />

      {/* Unsaved changes dialog for tab switching */}
      <Dialog
        open={unsavedDialogOpen}
        onClose={handleUnsavedDialogNo}
        aria-labelledby="unsaved-changes-dialog-title"
      >
        <DialogTitle id="unsaved-changes-dialog-title" sx={{ textAlign: 'center' }}>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Would you like to continue?
          </DialogContentText>
          {pendingImportConditions?.length > 0 && (
            <>
              <DialogContentText sx={{ color: 'text.primary', mt: 2 }}>
                The following imported cutoff(s) have not been saved and will be lost:
              </DialogContentText>
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                {pendingImportConditions.map((condition, i) => (
                  <li key={condition.orphanetCode || condition.acronym || condition.Acronym || condition.name || i}>
                    <Typography variant="body2">{getCutoffConditionDisplayName(condition)}</Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnsavedDialogYes} variant="contained" color="primary">
            Yes
          </Button>
          <Button onClick={handleUnsavedDialogNo} variant="outlined">
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved changes dialog for route navigation */}
      <Dialog
        open={blocker?.state === 'blocked'}
        onClose={handleCancelNavigation}
        aria-labelledby="nav-unsaved-dialog-title"
      >
        <DialogTitle id="nav-unsaved-dialog-title" sx={{ textAlign: 'center' }}>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave this page?
          </DialogContentText>
          {pendingImportConditions?.length > 0 && (
            <>
              <DialogContentText sx={{ color: 'text.primary', mt: 2 }}>
                The following imported cutoff(s) have not been saved and will be lost:
              </DialogContentText>
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                {pendingImportConditions.map((condition, i) => (
                  <li key={condition.orphanetCode || condition.acronym || condition.Acronym || condition.name || i}>
                    <Typography variant="body2">{getCutoffConditionDisplayName(condition)}</Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAcceptNavigation} variant="contained" color="primary">
            Yes, Leave
          </Button>
          <Button onClick={handleCancelNavigation} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cutoffDeleteConfirmOpen}
        onClose={handleCancelDeleteCutoff}
        aria-labelledby="cutoff-delete-confirm-title"
      >
        <DialogTitle id="cutoff-delete-confirm-title" sx={{ textAlign: 'center' }}>Delete Cutoff</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the selected cutoff?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmDeleteCutoff}
            variant="contained"
            color="primary"
            disabled={cutoffDeleteSaving}
            startIcon={cutoffDeleteSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {cutoffDeleteSaving ? 'Deleting...' : 'Yes'}
          </Button>
          <Button
            onClick={handleCancelDeleteCutoff}
            variant="outlined"
            disabled={cutoffDeleteSaving}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete ALL manual cutoffs confirmation dialog */}
      <Dialog
        open={deleteAllCutoffsConfirmOpen}
        onClose={handleCancelDeleteAllCutoffs}
        aria-labelledby="delete-all-cutoffs-confirm-title"
      >
        <DialogTitle id="delete-all-cutoffs-confirm-title">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningAmber sx={{ color: 'warning.main' }} />
            <span>Delete All Cutoffs</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            Are you sure you want to delete all {deleteAllCutoffCount} manually specified{' '}
            {deleteAllCutoffCount === 1 ? 'cutoff' : 'cutoffs'}? This will remove every
            condition from the manual cutoffs table.{' '}
            <Box component="span" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
              This action cannot be undone once you click Apply.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDeleteAllCutoffs} variant="contained" color="error">
            Delete All
          </Button>
          <Button onClick={handleCancelDeleteAllCutoffs} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning dialog for disabling conditions with manual cutoffs */}
      <Dialog
        open={cutoffWarningDialogOpen}
        onClose={handleCancelCutoffWarning}
        aria-labelledby="cutoff-warning-dialog-title"
      >
        <DialogTitle id="cutoff-warning-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningAmber sx={{ color: 'warning.main' }} />
            <span>Warning</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
            Warning! You are about to disable the following conditions that have manual cutoffs set:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, mb: 1 }}>
            {cutoffWarningConditions.map((condition) => (
              <li key={condition.orphanetCode || condition.acronym || condition.name}>
                <Typography variant="body2">
                  {getCutoffConditionDisplayName(condition)}
                </Typography>
              </li>
            ))}
          </Box>
          <DialogContentText sx={{ color: 'text.primary', mt: 2 }}>
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmCutoffWarning}
            variant="contained"
            color="primary"
            disabled={conditionsLoading}
            startIcon={conditionsLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            OK
          </Button>
          <Button
            onClick={handleCancelCutoffWarning}
            variant="outlined"
            disabled={conditionsLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import cutoffs validated — pending apply dialog */}
      <Dialog
        open={importCutoffsValidatedOpen}
        onClose={() => setImportCutoffsValidatedOpen(false)}
        aria-labelledby="import-cutoffs-validated-title"
      >
        <DialogTitle id="import-cutoffs-validated-title" sx={{ textAlign: 'center' }}>Validation Successful</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The data has been validated. Please review and{' '}
            <Box component="span" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
              click Apply to save
            </Box>{' '}
            the imported cutoffs.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportCutoffsValidatedOpen(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import cutoffs validated — no changes dialog */}
      <Dialog
        open={importCutoffsNoChangesOpen}
        onClose={() => setImportCutoffsNoChangesOpen(false)}
        aria-labelledby="import-cutoffs-no-changes-title"
      >
        <DialogTitle id="import-cutoffs-no-changes-title" sx={{ textAlign: 'center' }}>Validation Successful</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <WarningAmber sx={{ color: 'warning.main', mt: '2px', flexShrink: 0 }} />
            <DialogContentText>
              The file was uploaded successfully, but no cutoffs were added because the cutoffs defined in this file already exist.
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportCutoffsNoChangesOpen(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import cutoffs error dialog */}
      <Dialog
        open={importCutoffsErrorOpen}
        onClose={() => setImportCutoffsErrorOpen(false)}
        aria-labelledby="import-cutoffs-error-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="import-cutoffs-error-title">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: 'error.main' }} />
            <span>Import Failed</span>
          </Box>
        </DialogTitle>
        <DialogContent tabIndex={0}>
          <DialogContentText sx={{ color: 'text.primary', textAlign: 'center' }}>
            The import file contains errors. Please review the errors below and check the file data.
          </DialogContentText>
          <Box component="ul" sx={{ mt: '1.5rem', pl: '6rem', pr: '5rem' }}>
            {importCutoffsErrorMessages.map((msg, i) => (
              <li key={i}>
                <Typography variant="body2">{msg}</Typography>
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportCutoffsErrorOpen(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProgramSettingsDialogs;
